import { DataTable } from "@/components/molecules/tables/data-table";
import { useClientTeam } from "@/features/clients/hooks/use-clients";
import { useTranslation } from "@/features/utils/i18n";
import { useParams } from "react-router-dom";
import { PageLayout } from "../layout";
import { useAccessColumns } from "./components/columns";
import { InviteMemberDialog } from "./components/invite-dialog";

/**
 * The users of this tenant: who can sign in, and which team member each login is.
 *
 * A setting, not a resource: nothing here is readable from a call, and nobody
 * on this page is on the team by virtue of being on it. The team lives under
 * Ressources (`../members`), because that is what the agent draws on.
 *
 * The "on the team" column is the only place the two objects meet. A user
 * attached to nobody administers the tenant without being part of what the
 * agent talks about — an accountant, an integrator — and is reachable for a
 * routing rule on the phone of their own account, never as a team member.
 */
export function UsersPage() {
  const { t } = useTranslation("members");
  const { company: clientId } = useParams<{ company: string }>();
  const { data, isLoading } = useClientTeam(clientId || "");

  // The team is read here too: it is what the attachment column offers, and
  // sharing the query means the two pages cannot disagree about who exists.
  const columns = useAccessColumns(data?.members || []);

  return (
    <PageLayout
      width="medium"
      title={t("users.title")}
      description={t("users.description")}
      actions={<InviteMemberDialog />}
    >
      <DataTable
        columns={columns}
        data={data?.accesses || []}
        isLoading={isLoading}
        hideSearch={true}
        getRowId={(row) => row.access.id}
      />
    </PageLayout>
  );
}
